import { useCallback, useContext } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { Currency } from '@swf/core/enums';
import useRequest from '@swf/core/hooks/useRequest';
import type { Dispatch } from '@swf/core/store/types';
import { PokerModalRoutes } from '@swf/core/types/Poker';
import type {
  CreateClubTournamentResponse,
  HGCreateClubTournamentReply
} from '@swf/generated/types/api/v0/SwHomeGames';
import { handleServerResponse } from '@swf/poker';

import CreateTournamentContext from '../CreateTournamentContext/CreateTournamentContext';
import { getRecurringOptions } from '../Recurrence/utils/utils';
import type { FormValues } from '../utils/common';

import { TXTSRV_SorryX_we_are_experiencing_tem_ELL } from './useCreateClubTournament.messages.json';

const useCreateClubTournament = () => {
  const request = useRequest();
  const { formatMessage } = useIntl();
  const { modal } = useDispatch<Dispatch>();
  const {
    state: { clubId, widgetId },
    setState
  } = useContext(CreateTournamentContext);

  return {
    createClubTournament: useCallback(
      async ({ values, data: { tournamentStructuresResponse } }) => {
        setState((prevState) => ({ ...prevState, loading: true }));

        const {
          gameName,
          startDate,
          startTime,
          handType,
          potStruct,
          tableSize,
          currency,
          buyIn,
          startingChips,
          payout,
          tournFormat,
          tournLateRegLevel,
          levelLength,
          tournReentry,
          tournSyncBreaks,
          repeat,
          enableVideoChat,
          countTowardsClubStandings
        }: FormValues = values;

        // Safari Date object supports yyyy/MM/dd format
        const date = new Date(startDate.replace(/-/g, '/'));
        const year = date.getUTCFullYear();
        const month = (date.getMonth() + 1).toString();
        const day = date.getDate().toString();
        const errorMessage = formatMessage(TXTSRV_SorryX_we_are_experiencing_tem_ELL);
        let hour = '';
        let minute = '';

        // initial value is Date object
        if (typeof startTime === 'object') {
          const startTimeFormatted = new Date(startTime);
          hour = startTimeFormatted.getHours().toString();
          minute = startTimeFormatted.getMinutes().toString();
        } else {
          [hour, minute] = startTime.split(':');
        }

        const recurringTournament = repeat ? getRecurringOptions(values) : undefined;
        const handTypeValues = JSON.parse(handType as string);
        const buyInValues = JSON.parse(buyIn as string);

        const response = await request({
          service: 'homeGames.createClubTournament',
          body: {
            createClubTournament: {
              createTournParams2: {
                createTournParams: {
                  clubId,
                  gameName,
                  countTowardsClubStandings,
                  hiLo: handTypeValues.isHiLo,
                  tableSize,
                  isFastDealing: tournamentStructuresResponse.data.tournFastDealing[0],
                  currency,
                  handType: handTypeValues.handType,
                  potStruct,
                  buyIn: buyInValues.buyIn,
                  payout,
                  startingChips,
                  tournFormat,
                  levelLength,
                  tournKnockoutPrize: tournamentStructuresResponse.data.tournKnockoutPrize,
                  tournMaxPlayers: tournamentStructuresResponse.data.tournMaxPlayers,
                  scalePM: buyInValues.scalePM,
                  syncBreaksOn: tournSyncBreaks,
                  playMoney: currency === Currency.PlayMoney,
                  year,
                  month,
                  day,
                  hour,
                  minute
                },
                enableVideoChat,
                lateRegLevels: tournLateRegLevel,
                reentry: tournReentry
              },
              recurringTournament
            }
          }
        })
          .toPromise()
          .catch(() => {
            setState((prevState) => ({
              ...prevState,
              error: errorMessage
            }));

            return Promise.reject();
          });

        setState((prevState) => ({ ...prevState, loading: false }));

        if (!response || !response.body) {
          setState((prevState) => ({
            ...prevState,
            error: errorMessage
          }));

          return Promise.reject();
        }

        const createClubTournamentResponse = handleServerResponse<
          CreateClubTournamentResponse,
          HGCreateClubTournamentReply
        >({
          response: response.body,
          accessor: 'createClubTournamentReply'
        });
        if (!createClubTournamentResponse.error) {
          modal.replace([
            { path: PokerModalRoutes.createTournamentSuccess, locationState: { widgetId } }
          ]);
          return { createClubTournamentResponse };
        }

        setState((prevState) => ({ ...prevState, error: `${createClubTournamentResponse.error}` }));

        return Promise.reject();
      },
      [setState, request, clubId, formatMessage, modal, widgetId]
    )
  };
};

export default useCreateClubTournament;
